import React, { useEffect, useState, useCallback } from 'react';
import { Truck } from 'lucide-react';
import { inv } from '../../lib/invApi';
import { toast } from 'sonner';
import { VanDemandTrends } from '../../components/inventory/VanDemandTrends';

const num = (n) => (Number.isInteger(n) ? n : Number(n || 0).toFixed(2));
const money = (n) => (n === null || n === undefined ? '—' : `$${Number(n).toFixed(2)}`);
const sel = 'h-9 rounded-md bg-[#0A0A0A] border border-[#262626] text-white text-sm px-2';
const PRESETS = [{ k: 7, l: '7 days' }, { k: 30, l: '30 days' }, { k: 90, l: '90 days' }, { k: 'custom', l: 'Custom' }];

const Totals = ({ label, t }) => (
  <div className="text-xs"><p className="text-[#525252] uppercase tracking-wider text-[10px]">{label}</p>
    <p className="text-white">{num(t.quantity)} pcs · {t.transfers} transfer{t.transfers === 1 ? '' : 's'}</p>
    <p className="text-[#A3A3A3]">{money(t.value)}</p></div>
);

// Van Transfer History — factual material pulled from the Shop INTO each van (from the
// movement ledger). A transfer is not confirmed field usage.
const InventoryVanTransfers = () => {
  const [van, setVan] = useState('');
  const [preset, setPreset] = useState(30);
  const [range, setRange] = useState({ start: '', end: '' });
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const params = { location_id: van || undefined };
    if (preset === 'custom') { if (!range.start) return; params.start = range.start; params.end = range.end || undefined; } else params.days = preset;
    setData(null);   // never show stale results under new filters
    try { const { data } = await inv.get('/van-transfers', params); setData(data); } catch (e) { setData({ error: e.response?.data?.detail || 'Could not load' }); toast.error(e.response?.data?.detail || 'Could not load'); }
  }, [van, preset, range]);
  useEffect(() => { load(); }, [load]);

  const vans = data?.vans || [];
  const [allVans, setAllVans] = useState([]);
  useEffect(() => { if (data?.all_vans) setAllVans(data.all_vans); }, [data]);
  return (
    <div data-testid="inv-van-transfers-page" className="space-y-5">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1 flex items-center gap-3"><Truck className="w-7 h-7 text-[#D4AF37]" /> Van Transfer History</h2>
        <p className="text-[#A3A3A3] text-sm">What material was transferred from the Shop/Warehouse into each van (Shop Outflow / Van Material Demand). A transfer is a fact — it is <b>not</b> confirmed use on a job, and a second transfer does not mean the first was used up.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="inv-vt-van" value={van} onChange={(e) => setVan(e.target.value)} className={sel}>
          <option value="">All vans</option>
          {allVans.map((v) => <option key={v.location_id} value={v.location_id}>{`${v.name}${v.archived ? ' (archived)' : ''}`}</option>)}
        </select>
        {PRESETS.map((p) => (
          <button key={p.k} type="button" data-testid={`inv-vt-preset-${p.k}`} onClick={() => setPreset(p.k)}
            className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${preset === p.k ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37]' : 'border-[#262626] text-[#A3A3A3] hover:text-white'}`}>{p.l}</button>
        ))}
        {preset === 'custom' && <>
          <input data-testid="inv-vt-start" type="date" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} className={sel} />
          <input data-testid="inv-vt-end" type="date" value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} className={sel} />
        </>}
      </div>
      {!data ? <p className="text-[#A3A3A3]">Loading…</p> : data.error ? <p data-testid="inv-vt-error" className="text-amber-300 text-sm">{data.error}</p> : <>
        <p data-testid="inv-vt-period" className="text-sm text-white">{data.period.start} → {data.period.end} <span className="text-[#A3A3A3] text-xs">(Eastern Time)</span>: <b>{num(data.period.quantity)}</b> pcs in {data.period.transfers} transfer{data.period.transfers === 1 ? '' : 's'} · {money(data.period.value)}</p>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="inv-vt-vans">
          {vans.filter((v) => v.totals_90d.transfers > 0 || van).map((v) => (
            <div key={v.location_id} data-testid={`inv-vt-van-card-${v.location_id}`} className="bg-[#141414] border border-[#262626] rounded-md p-3">
              <p className="text-white font-semibold">{v.name}{v.assigned_employee_name ? <span className="text-[#A3A3A3] font-normal text-xs ml-2">{v.assigned_employee_name}</span> : null}</p>
              <div className="grid grid-cols-3 gap-2 mt-2"><Totals label="7 days" t={v.totals_7d} /><Totals label="30 days" t={v.totals_30d} /><Totals label="90 days" t={v.totals_90d} /></div>
            </div>
          ))}
        </div>
        <section>
          <h3 className="text-white text-sm uppercase tracking-wider mb-2">Most frequently transferred</h3>
          <div className="flex flex-wrap gap-2" data-testid="inv-vt-top">
            {data.top_items.length === 0 ? <span className="text-[#525252] text-sm">No transfers in this period.</span> : data.top_items.map((t) => (
              <span key={t.item_id} className="text-xs px-2.5 py-1 rounded-full border border-[#262626] text-white">{t.item_name} <span className="text-[#A3A3A3]">· {num(t.quantity)} pcs · {t.transfers}×</span></span>
            ))}
          </div>
        </section>
        <VanDemandTrends vanId={van} />
        <div className="bg-[#141414] border border-[#262626] rounded-md overflow-auto max-h-[60vh]">
          <table className="w-full text-sm min-w-[820px]" data-testid="inv-vt-table">
            <thead className="sticky top-0 bg-[#0A0A0A]"><tr className="text-[#A3A3A3] border-b border-[#262626]">
              {['Date / Time', 'Van', 'Item', 'Qty Transferred In', 'Source', 'Employee', 'Unit Cost', 'Value'].map((h, i) => <th key={h} className={`px-3 py-2 font-medium ${[3, 6, 7].includes(i) ? 'text-right' : 'text-left'}`}>{h}</th>)}
            </tr></thead>
            <tbody>
              {data.rows.length === 0 ? <tr><td colSpan={8} className="px-3 py-6 text-center text-[#525252]">No transfers into vans in this period.</td></tr> : data.rows.map((r) => (
                <tr key={r.id} data-testid={`inv-vt-row-${r.id}`} className="border-b border-[#262626] last:border-0">
                  <td className="px-3 py-2 text-[#A3A3A3] whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleString('en-US', { timeZone: 'America/New_York' }) : ''}</td>
                  <td className="px-3 py-2 text-white">{r.to_location_name}</td>
                  <td className="px-3 py-2 text-white">{r.item_name}{r.item_sku && <span className="text-[#525252] text-xs ml-2">{r.item_sku}</span>}</td>
                  <td className="px-3 py-2 text-right text-[#D4AF37] font-semibold">{num(r.quantity)}</td>
                  <td className="px-3 py-2 text-[#A3A3A3]">{r.from_location_name}</td>
                  <td className="px-3 py-2 text-[#A3A3A3]">{r.user_name}</td>
                  <td className="px-3 py-2 text-right text-[#A3A3A3]">{money(r.unit_cost)}</td>
                  <td className="px-3 py-2 text-right text-white">{money(r.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>}
    </div>
  );
};

export default InventoryVanTransfers;
